import { Autocomplete, Button, Input } from "src/components";
import crossIcon from "src/assets/cross.svg";
import { useToast } from "src/hooks/useToast";
import { useEffect, useState } from "react";
import { generateReport, getCompanyList } from "src/services/order-api";
import { getCounterDetails } from "src/services/reports-api";

const OrderDetailsReportFilters = ({
    onCloseModal,
    onSuccess,
}: {
    onCloseModal: () => void;
    onSuccess: () => void
}) => {
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<any>(null);
    const [fromDate, setFromDate] = useState<string>("");
    const [toDate, setToDate] = useState<string>("");
    const [companyData, setCompanyData] = useState<any>([]);
    const [selectedCounter, setSelectedCounter] = useState<any>(null);
    const [counterData, setCounterData] = useState<any>([]);

    const fetchCounter = async (value: any) => {
        const payload = {
            company_id: selectedCompany?.id,
            counter_code: value,
        };

        try {
            const res = await getCounterDetails(payload);
            setCounterData(res?.data?.data);
            return res?.data?.data;
        } catch (error) {
            console.log(error);
        }
    };

    const handleGenerateReport = async () => {
        if (!selectedCompany?.code) {
            toast({ description: "Please select company", variant: "destructive" });
            return;
        }
        if (!fromDate || !toDate) {
            toast({ description: "Please select from and to date", variant: "destructive" });
            return;
        }
        if (new Date(toDate) < new Date(fromDate)) {
            toast({ description: "To date cannot be before From date", variant: "destructive" });
            return;
        }
        if (!selectedCounter?.counter_code) {
            toast({ description: "Please select counter", variant: "destructive" });
            return;
        }
        const payload = {
            "report_type": "order_by_counter",
            "time_range": fromDate + "-" + toDate,
            "filters": {
                counter_code: [selectedCounter?.counter_code]
            },
            "company_code": selectedCompany?.code,
            "company_name": selectedCompany?.name
        }
        setIsLoading(true);
        try {
            const response = await generateReport(payload);
            console.log(response);
            toast({ description: "Report generation request sent successfully", variant: "default" });
            onSuccess();
        } catch (error: any) {
            console.log(error);
            toast({ description: error?.response?.data?.message || "Failed to generate report", variant: "destructive" });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (!companyData?.length) {
            getCompanyList()
                .then((res) => setCompanyData(res?.data))
                .catch((err) => console.log(err));

        }
    }, []);

    return (
        <div className="flex flex-col gap-y-2 w-[400px] mx-auto bg-white p-4 border border-gray-300 shadow-lg rounded-lg">
            <div className="flex justify-between items-center">
                <p className="text-normal py-1 font-semibold text-[#1B1A1F]">Generate Report</p>
                <Button className="bg-transparent -mr-3" onClick={onCloseModal}>
                    <img src={crossIcon} alt="cross" className="w-5 h-5 font-bold invert cursor-pointer" />
                </Button>
            </div>
            <div className="flex flex-col gap-y-2 px-2">
                <div className="mb-2">
                    <label className="text-sm font-normal text-[#666666]">Company Name</label>
                    <Autocomplete
                        onSelectionChange={(value: any) => { setSelectedCompany(value) }}
                        placeholder="Select Company"
                        inputClassName="w-full mt-2"
                        suggestionList={companyData || []}
                        displayKey="name"
                        defaultValue={selectedCompany}
                        uniqueKey="code" />
                </div>
                <div className="mb-2">
                    <label className="text-sm font-normal text-[#666666] w-fit">Counter</label>
                    <Autocomplete
                        onSelectionChange={(value: any) => { setSelectedCounter(value) }}
                        placeholder="Select Counter"
                        inputClassName="w-full min-w-[12rem]"
                        suggestionList={counterData || []}
                        displayKey="counter_name"
                        defaultValue={selectedCounter}
                        isAsyncQuery={true}
                        asyncListFunction={fetchCounter}
                        additionalDisplayKey="counter_code"
                        additionalDisplayKeySearch={true}
                        uniqueKey="counter_code" />
                </div>


                <div className="flex justify-between mt-2">
                    <div className="w-[45%]">From: <Input
                        enableArrows={false}
                        showIcon={false}
                        type="date"
                        placeholder="E-Way Bill Date"
                        value={fromDate ? fromDate.split("T")[0] : ""}
                        onChange={(e: any) => {
                            const dateValue = e.target.value;
                            const selectedDate = new Date(dateValue);

                            const now = new Date();

                            // Set time using UTC values
                            selectedDate.setUTCHours(now.getUTCHours());
                            selectedDate.setUTCMinutes(now.getUTCMinutes());
                            selectedDate.setUTCSeconds(now.getUTCSeconds());
                            selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());

                            const formattedDate = selectedDate.toISOString(); // This will be in UTC
                            setFromDate(formattedDate);
                        }}
                        max={new Date().toISOString().split("T")[0]}
                        className="w-full py-1 border border-gray-300 rounded-lg"
                    /></div>
                    <div className="w-[45%]">To: <Input
                        enableArrows={false}
                        showIcon={false}
                        type="date"
                        placeholder="E-Way Bill Date"
                        value={toDate ? toDate.split("T")[0] : ""}
                        onChange={(e: any) => {
                            const dateValue = e.target.value;
                            const selectedDate = new Date(dateValue);

                            const now = new Date();

                            // Set time using UTC values
                            selectedDate.setUTCHours(now.getUTCHours());
                            selectedDate.setUTCMinutes(now.getUTCMinutes());
                            selectedDate.setUTCSeconds(now.getUTCSeconds());
                            selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());

                            const formattedDate = selectedDate.toISOString(); // This will be in UTC
                            setToDate(formattedDate);
                        }}
                        max={new Date().toISOString().split("T")[0]}
                        className="w-full py-1 border border-gray-300 rounded-lg"
                    /></div>
                </div>

            </div>

            <div className="gap-x-2">
                <Button isLoading={isLoading} loaderSize="small" disabled={isLoading} className="text-white w-full bg-[#8051FF] mt-4" onClick={handleGenerateReport}>
                    Submit
                </Button>

            </div>
        </div >
    );
};

export default OrderDetailsReportFilters;
